import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import Pagination from "../../components/ui/Pagination";
import { useAssets, useAssetTypes } from "../../api/queries/assetQueries";
import useAssetAccess from "../../hooks/useAssetAccess";
import { getApiErrorMessage } from "../../utils/apiError";
import { todayInManila, addDaysToPlainDate } from "../../utils/dateTime";
import {
  AssetStatusBadge,
  ConditionText,
  ASSET_STATUSES,
} from "./components/AssetBadges";
import AssetActionsMenu from "./components/AssetActionsMenu";
import AssetFormDrawer from "./components/AssetFormDrawer";
import BranchSelect from "../../components/ui/BranchSelect";
import { useAssetActions } from "./components/AssetActionDialogs";
import { employeeName } from "./components/assetErrors";
import { WarrantyText } from "./components/AssetDisplay";
import "./assets.css";
import usePermissions from "../../hooks/usePermissions";

const FILTER_KEYS = [
  "search",
  "status",
  "assetTypeId",
  "branchId",
  "warranty",
  "includeDeleted",
];

const AssetRegisterPage = () => {
  const navigate = useNavigate();
  // HR-level (assets with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("assets");
  const { canModifyAssets } = useAssetAccess();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page")) || 1;
  const limit = Number(params.get("limit")) || 20;
  const filters = useMemo(
    () => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""])),
    [params],
  );

  const [searchInput, setSearchInput] = useState(filters.search);
  const [drawer, setDrawer] = useState(null); // { asset } for edit, {} for register
  const { openAction, dialogs } = useAssetActions();
  const { data: types = [] } = useAssetTypes(true);

  const update = (changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) =>
      v ? next.set(k, String(v)) : next.delete(k),
    );
    if (resetPage && !("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  };

  // Debounced search (300ms)
  useEffect(() => {
    if (searchInput === filters.search) return undefined;
    const t = setTimeout(() => update({ search: searchInput.trim() }), 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useAssets({
    search: filters.search,
    status: filters.status,
    assetTypeId: filters.assetTypeId,
    branchId: filters.branchId,
    // "Expiring in N days" → warrantyExpiringBefore = today + N
    warrantyExpiringBefore: filters.warranty
      ? addDaysToPlainDate(todayInManila(), Number(filters.warranty))
      : "",
    includeDeleted: filters.includeDeleted === "true" || undefined,
    page,
    limit,
  });
  const rows = query.data?.data || [];
  const total = query.data?.total || 0;

  const handleAction = (asset, key) => {
    if (key === "edit") return setDrawer({ asset });
    if (key.startsWith("status:"))
      return openAction("status", { asset, status: key.split(":")[1] });
    return openAction(key, { asset });
  };

  const hasFilters = FILTER_KEYS.some((k) => filters[k]);

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">Asset Register</h1>
            <p className="text-muted small mb-0">
              Company equipment, where it is, and who has it.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Link
              to="/assets/assignments"
              className="btn btn-sm px-3 fw-semibold d-flex align-items-center profile-btn-white h-35px rounded-8"
            >
              <i className="bi bi-person-badge me-2"></i> Assignments
            </Link>
            {isCompanyWide && (
              <Link
                to="/settings/asset-types"
                className="btn btn-sm px-3 fw-semibold d-flex align-items-center profile-btn-white h-35px rounded-8"
              >
                <i className="bi bi-tags me-2"></i> Asset types
              </Link>
            )}
            {canModifyAssets && (
              <button
                type="button"
                className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
                onClick={() => setDrawer({})}
              >
                <i className="bi bi-plus-lg me-2"></i> Register Asset
              </button>
            )}
          </div>
        </div>

        {/* Toolbar */}
        <div className="asset-toolbar mb-3">
          <div className="position-relative asset-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
            <input
              type="search"
              className="filter-input w-100 ps-5"
              placeholder="Search tag, name, serial, brand, model"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search assets"
            />
          </div>
          <select
            className="filter-input"
            value={filters.status}
            onChange={(e) => update({ status: e.target.value })}
            aria-label="Status"
          >
            <option value="">All statuses</option>
            {ASSET_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            className="filter-input"
            value={filters.assetTypeId}
            onChange={(e) => update({ assetTypeId: e.target.value })}
            aria-label="Type"
          >
            <option value="">All types</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.isActive ? "" : " (inactive)"}
              </option>
            ))}
          </select>
          <div className="asset-branch-filter">
            <BranchSelect
              value={filters.branchId}
              onChange={(v) => update({ branchId: v })}
              emptyLabel="All branches"
            />
          </div>
          <select
            className="filter-input"
            value={filters.warranty}
            onChange={(e) => update({ warranty: e.target.value })}
            aria-label="Warranty"
          >
            <option value="">Any warranty</option>
            <option value="30">Expiring in 30 days</option>
            <option value="60">Expiring in 60 days</option>
            <option value="90">Expiring in 90 days</option>
          </select>
          <div className="form-check form-switch m-0">
            <input
              id="show-deleted"
              className="form-check-input"
              type="checkbox"
              checked={filters.includeDeleted === "true"}
              onChange={(e) =>
                update({ includeDeleted: e.target.checked ? "true" : "" })
              }
            />
            <label htmlFor="show-deleted" className="form-check-label fs-080">
              Show deleted
            </label>
          </div>
          {hasFilters && (
            <button
              type="button"
              className="btn btn-link fs-075 text-slate-500 p-0"
              onClick={() => {
                setSearchInput("");
                update(Object.fromEntries(FILTER_KEYS.map((k) => [k, ""])));
              }}
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0 asset-table">
              <thead className="custom-table-header">
                <tr>
                  <th className="ps-4 py-3">Tag</th>
                  <th className="py-3">Name</th>
                  <th className="py-3">Type</th>
                  <th className="py-3">Branch</th>
                  <th className="py-3">Status</th>
                  <th className="py-3">Condition</th>
                  <th className="py-3">Current holder</th>
                  <th className="py-3">Warranty</th>
                  <th className="py-3 pe-4 text-end">Actions</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {rows.map((a) => (
                  <tr
                    key={a.id}
                    className={`cursor-pointer ${a.deletedAt ? "asset-row-deleted" : ""}`}
                    onClick={() => navigate(`/assets/${a.id}`)}
                  >
                    <td className="ps-4 fw-bold text-navy text-nowrap">
                      {a.assetTag}
                    </td>
                    <td>
                      <div className="fw-semibold">{a.name}</div>
                      <div className="text-slate-500 fs-075">
                        {[a.brand, a.model].filter(Boolean).join(" ") || "—"}
                      </div>
                    </td>
                    <td>{a.assetType?.name || "—"}</td>
                    <td>
                      {a.branch?.branchName || (
                        <span className="text-slate-500">Company level</span>
                      )}
                    </td>
                    <td>
                      <AssetStatusBadge status={a.status} />
                    </td>
                    <td>
                      <ConditionText condition={a.condition} />
                    </td>
                    <td>
                      {a.currentEmployee ? (
                        <>
                          <div className="fw-semibold">
                            {employeeName(a.currentEmployee)}
                          </div>
                          <div className="text-slate-500 fs-075">
                            {a.currentEmployee.employeeId}
                          </div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="text-nowrap">
                      <WarrantyText date={a.warrantyExpiryDate} />
                    </td>
                    <td className="pe-4 text-end">
                      <AssetActionsMenu
                        asset={a}
                        onAction={(key) => handleAction(a, key)}
                      />
                    </td>
                  </tr>
                ))}
                {!query.isLoading && !query.isError && rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-5 text-slate-500">
                      <i className="bi bi-box-seam d-block fs-200 text-slate-300 mb-2"></i>
                      {hasFilters
                        ? "No assets match these filters."
                        : "No assets registered yet."}
                    </td>
                  </tr>
                )}
                {query.isError && (
                  <tr>
                    <td colSpan={9} className="text-center py-5 text-danger">
                      {getApiErrorMessage(
                        query.error,
                        "Could not load assets.",
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {query.isLoading && (
            <div className="text-center py-5">
              <span
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading"
              ></span>
            </div>
          )}
          <Pagination
            page={page}
            limit={limit}
            total={total}
            onPageChange={(p) => update({ page: p }, { resetPage: false })}
            onLimitChange={(l) => update({ limit: l })}
          />
        </div>
      </div>

      {drawer && (
        <AssetFormDrawer
          asset={drawer.asset || null}
          onClose={() => setDrawer(null)}
        />
      )}
      {dialogs}
    </Layout>
  );
};

export default AssetRegisterPage;
